import React from "react";

import Icon from "@mdi/react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { OrganizationFastPlanOptions } from "@/src/types/organization.types";

import CardDeleteButton from "./CardDeleteButton";
import { iconByStreamType } from "./CardOttAsset";
import CardQualityBadge from "./CardQualityBadge";
import StatusBadge from "./StatusBadge";

import type { FastAsset } from "../../../api/MediaAPI.types";
import type { AppState } from "../../../reducers";

interface CardFastAssetProps {
  fastAsset: FastAsset;
  onDelete: () => void;
}

export default function CardFastAsset({
  fastAsset,
  onDelete,
}: CardFastAssetProps): React.JSX.Element {
  const { t } = useTranslation("editor-encoding");

  const typeBadgeClassNames = classnames(
    "self-center mt-0.5 rounded px-2 py-1 text-[0.78125rem] font-semibold",
    "bg-status-active/10 text-status-active",
  );

  const organizationOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const hasFastPlus =
    organizationOptions?.fast_plan === OrganizationFastPlanOptions.FAST_PLUS;

  const statusesToCheck = hasFastPlus
    ? (["hls_status", "mp4_status"] as const)
    : (["mp4_status"] as const);

  const canDelete =
    fastAsset.uuid &&
    statusesToCheck.every(
      (statusKey) =>
        fastAsset[statusKey] === "transcoded" ||
        fastAsset[statusKey] === "error",
    );

  return (
    <div className="border-border bg-card flex items-start gap-4 rounded-lg border p-3">
      <div className={typeBadgeClassNames}>
        <Icon path={iconByStreamType.fast} size={1.25} />
      </div>
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-h-7 items-center pb-1">
            <p className="truncate text-sm">{t("fast")}</p>
          </div>
          <CardQualityBadge qualityLabel="FAST" />
        </div>
        <div className="flex shrink-0 items-start gap-2">
          <div className="flex flex-col items-end gap-1">
            {hasFastPlus ? (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-[0.78125rem]">
                  {t("fast_hls")}
                </span>
                <StatusBadge
                  progression={fastAsset.hls_encoding_progression}
                  status={fastAsset.hls_status}
                />
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-[0.78125rem]">
                {t("fast_mp4")}
              </span>
              <StatusBadge
                progression={fastAsset.mp4_encoding_progression}
                status={fastAsset.mp4_status}
              />
            </div>
          </div>
          {canDelete ? (
            <CardDeleteButton
              cancelLabel={t("cancel")}
              confirmLabel={t("deleteFastAssetConfirm")}
              message={t("deleteFastAssetMessage")}
              onDelete={onDelete}
              title={t("deleteFastAssetTitle")}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
